import { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router";

import { useAppDispatch, useAppSelector } from "../../../hooks";
import { usePrevious } from "../../../hooks";
import {
  selectActiveCompetitorIdsSignatureState,
  selectSelectedBrandIdState,
  selectViewBrandIdState,
  selectListBrandsState,
  selectBrandLoading,
  selectAddNewBrandBrandState,
} from "../../../store/selectors/brands";
import { selectAuthAccountId } from "../../../store/selectors/auth";
import { fetchBrands, fetchSelectedBrandId } from "../../../store/brands/slice";
import { fetchModels } from "../../../store/models/slice";
import { fetchPrompts } from "../../../store/prompts/slice";
import {
  fetchBrand7dSummary,
  fetchLastUpdatesBrand,
} from "../../../store/scores/slice";
import { isFiniteBrandId } from "../../../store/utils/brandId";
import { ERoutes } from "../../../constants/routes";

export function useMainLayoutData(): void {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const userID = useAppSelector(selectAuthAccountId);
  const selectedBrandId = useAppSelector(selectSelectedBrandIdState);
  const viewBrandId = useAppSelector(selectViewBrandIdState);
  const competitorIdsSignature = useAppSelector(selectActiveCompetitorIdsSignatureState);
  const listBrands = useAppSelector(selectListBrandsState);
  const brandLoading = useAppSelector(selectBrandLoading);
  const addNewBrandBrand = useAppSelector(selectAddNewBrandBrandState);
  const previousUserID = usePrevious(userID);
  const previousSelectedBrandId = usePrevious(selectedBrandId);
  const lastFetchedLastUpdatesBrandIdRef = useRef<number | null>(null);

  // Redirect to "Add New Brand" when brands have been loaded but the list is empty.
  // This handles managed clients created by agencies who have 0 brands.
  useEffect(() => {
    const isAddBrandPage = location.pathname.startsWith(ERoutes.AddNewBrand.replace(/\/+$/, ''));
    if (!brandLoading && !addNewBrandBrand && listBrands !== null && listBrands.length === 0 && !isAddBrandPage) {
      navigate(ERoutes.AddNewBrand, { replace: true });
    }
  }, [brandLoading, listBrands, location.pathname, navigate]);

  useEffect(() => {
    if (userID != null && previousUserID !== userID) {
      dispatch(fetchSelectedBrandId());
    }
  }, [userID, previousUserID, dispatch]);

  // Always load the brand list when user changes — needed even for users with 0 brands
  // so the brand dropdown can show the "New Brand" button instead of a skeleton.
  useEffect(() => {
    if (userID != null && (previousUserID !== userID || previousSelectedBrandId !== selectedBrandId)) {
      dispatch(fetchBrands());
    }
  }, [userID, selectedBrandId, previousUserID, previousSelectedBrandId, dispatch]);

  // Models & prompts require a selected brand — only load when one is available
  useEffect(() => {
    if (
      (previousUserID !== userID || previousSelectedBrandId !== selectedBrandId) &&
      isFiniteBrandId(selectedBrandId)
    ) {
      dispatch(fetchModels());
      dispatch(fetchPrompts());
    }
  }, [userID, selectedBrandId, previousUserID, previousSelectedBrandId, dispatch]);

  useEffect(() => {
    if (selectedBrandId === undefined || competitorIdsSignature === null) return;
    dispatch(fetchBrand7dSummary());
  }, [selectedBrandId, competitorIdsSignature, dispatch]);

  useEffect(() => {
    if (selectedBrandId !== previousSelectedBrandId && previousSelectedBrandId !== undefined) {
      lastFetchedLastUpdatesBrandIdRef.current = null;
    }
  }, [selectedBrandId, previousSelectedBrandId]);

  useEffect(() => {
    if (viewBrandId == null) return;
    if (lastFetchedLastUpdatesBrandIdRef.current === viewBrandId) return;
    lastFetchedLastUpdatesBrandIdRef.current = viewBrandId;
    dispatch(fetchLastUpdatesBrand(viewBrandId));
  }, [dispatch, viewBrandId]);
}
